import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import translate from "@insite/client-framework/Translate";
import { DOCUMENT_TYPE } from "@store/constants";
import { contentSpacingStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
/* Poolcorp Custom Code */

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    // Poolcorp Custom Code
    list?: InjectableCss;
    documentTypeText?: TypographyPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {},
    documentGridItem: {},
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentLink: {
        typographyProps: {
            css: css`
                display: flex;
                align-items: flex-start;
            `,
        },
        css: css`
            ${contentSpacingStyles(null, "0.5em")}
            vertical-align: middle;

            &:hover {
                opacity: 0.5;
            }
        `,
    },
    documentNameText: {
        color: "text.main",
        css: css`
            text-decoration: underline;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.bold};
            word-break: break-all;
        `,
    },
    documentTypeText: {
        css: css`
            font-style: italic;
        `,
    },
    list: {
        css: css`
            display: flex;
            flex-direction: column;

            li {
                ${contentSpacingStyles(null, "0.25em")}
            }
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocumentsList: React.FC<OwnProps> = ({ product: { documents } }) => {
    const documentsWithoutSchematics = documents?.filter(
        document => document.documentType !== DOCUMENT_TYPE.schematics,
    ); // Poolcorp Custom Code

    if (!documentsWithoutSchematics?.length) {
        return <>{translate("No documents available")}</>;
    }

    return (
        <Typography as="ul" {...styles.list}>
            {documentsWithoutSchematics.map(document => (
                <Typography key={document.id.toString()} as="li">
                    <Link
                        href={document.filePath}
                        target="_blank"
                        {...styles.documentLink}
                        data-test-selector={`productDetails_${document.id}`}
                    >
                        <IconMemo {...styles.documentIcon} />
                        <Typography {...styles.documentNameText}>
                            {document.description || document.name}
                            {document.documentType && (
                                <Typography {...styles.documentTypeText}>
                                    {" "}
                                    ({translate(`DocumentType_${document.documentType}`)})
                                </Typography>
                            )}
                        </Typography>
                    </Link>
                </Typography>
            ))}
        </Typography>
    );
};

const ProductDetailsDocuments: React.FC<OwnProps> = props => {
    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductDetailsDocumentsList {...props} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
